import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bell } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { formatDateTime } from '@/lib/format'
import { messages } from '@/lib/messages'
import { queryKeys } from '@/lib/query-keys'
import { cn } from '@/lib/utils'
import { notificationsApi, type AppNotification } from '@/services/http/notifications'

const t = messages.notifications

/**
 * Üst çubuktaki zil: okunmamış sayısı, son bildirimler ve "tümünü okundu say". Yeni bildirim SSE
 * (`source=app`, `table=notifications`) ile düşer; köprü bu sorguyu tazeler. Bağlantı koparsa
 * dakikada bir yoklanır.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: queryKeys.notifications(),
    queryFn: notificationsApi.list,
    refetchInterval: 60_000,
  })
  const refresh = () => void queryClient.invalidateQueries({ queryKey: queryKeys.notifications() })
  const read = useMutation({
    mutationFn: (id: string) => notificationsApi.read(id),
    onSuccess: refresh,
  })
  const readAll = useMutation({ mutationFn: () => notificationsApi.readAll(), onSuccess: refresh })
  const unread = query.data?.unread ?? 0
  const items = query.data?.items ?? []

  const select = (item: AppNotification) => {
    if (!item.read_at) read.mutate(item.id)
    setOpen(false)
    if (item.link) void navigate(item.link)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={<Button variant="ghost" size="icon-sm" className="relative" />}
        aria-label={t.open(unread)}
      >
        <Bell />
        {unread > 0 && (
          <span
            aria-hidden="true"
            className="bg-brand text-brand-foreground absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] leading-none font-semibold tabular-nums"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="flex w-[min(24rem,calc(100vw-2rem))] flex-col p-0">
        <div className="border-border flex items-center justify-between gap-2 border-b px-3 py-2">
          <h2 className="text-sm font-medium">{t.title}</h2>
          <Button
            size="sm"
            variant="ghost"
            disabled={unread === 0 || readAll.isPending}
            onClick={() => readAll.mutate()}
          >
            {t.markAll}
          </Button>
        </div>
        {query.isError && <p className="text-danger px-3 py-4 text-sm">{t.failed}</p>}
        {query.isSuccess && items.length === 0 && (
          <p className="text-text-muted px-3 py-4 text-sm">{t.empty}</p>
        )}
        {items.length > 0 && (
          <ul className="flex max-h-96 flex-col overflow-y-auto">
            {items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className="hover:bg-surface-muted flex w-full gap-2 px-3 py-2.5 text-left"
                  onClick={() => select(item)}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      'mt-1.5 size-2 shrink-0 rounded-full',
                      item.read_at ? 'bg-transparent' : 'bg-brand',
                    )}
                  />
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className={cn('text-sm', !item.read_at && 'font-medium')}>
                      {item.title}
                    </span>
                    {item.body && (
                      <span className="text-text-secondary line-clamp-2 text-xs whitespace-pre-line">
                        {item.body}
                      </span>
                    )}
                    <span className="text-text-muted text-xs tabular-nums">
                      {formatDateTime(item.created_at)}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  )
}
